import Vue from 'vue'
import { Component, Emit, Prop } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import ConfigurationParametersGlobalService from '@/modules/configurationModule/detail/parameters/services/configuration-parameters-global.service'
import ConfigurationParametersLocalService from '@/modules/configurationModule/detail/parameters/services/configuration-parameters-local.service'
import { ConfigurationParameterFormState, initialConfigurationParameterFormState, SideBar, SideBarData } from '@/modules/configurationModule/detail/parameters/components/configuration-parameters-form/configuration-parameters-form.model'
import { ConfigurationParameter, ConfigurationParameterService, ConfigurationParameterType, ConfigurationParameterTypeEnum } from '@/modules/configurationModule/detail/parameters/models/configuration-parameters.model'
import { ConfigurationParameterGroupResponse, ConfigurationParameterOptionResponse, ConfigurationParameterPatchInBatchRequest, ConfigurationParameterResponse } from '@/modules/configurationModule/detail/parameters/services/configuration-parameters.dto'
import { isNil, startCase } from 'lodash'
import sessionStore from '@/session/sessionStore'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader
    }
})
export default class ConfigurationParametersForm extends Vue {
    @Prop() readonly serviceInstance!: () => Promise<ConfigurationParameterService>
    @Prop() readonly groupId!: number

    @Emit('updateSideBarData')
    updateSideBarData(): SideBarData {
        const sidebar: SideBar[] = this.state.parametersGroups?.map(parameterGroup => ({
            id: parameterGroup.id,
            label: parameterGroup.name,
            icon: 'fal fa-sliders-h',
            routeName: `ConfigurationParameters${startCase(this._mode)}`,
            routeParams: { id: parameterGroup.id },
            selected: parameterGroup.id === this.state.groupId,
            hasChildren: false
        }))

        return {
            title: this.$tc(`configuration.elementTitle.${this._mode}Parameters`),
            id: `${this._mode}Parameters`,
            sidebar
        }
    }

    state: ConfigurationParameterFormState

    get global(): boolean {
        return this.state.service instanceof ConfigurationParametersGlobalService
    }

    private get _mode(): string {
        return this.global ? 'global' : 'local'
    }

    get configurationParameterTypeEnum(): typeof ConfigurationParameterTypeEnum {
        return ConfigurationParameterTypeEnum
    }

    get modelValidated(): boolean {
        return this.$_someFieldIsDirty(this.fields)
    }

    constructor() {
        super()
        this.state = initialConfigurationParameterFormState(this.groupId)
    }

    private async _loadData(): Promise<void> {
        this.$_updateLoading(true)
        this.state.service = await this.serviceInstance()

        // load parameters group
        this.state.parametersGroups = await this._getConfigurationParametersGroup()
        if (this.state.parametersGroups.length) {
            this.state.parameterGroup = this.state.parametersGroups.find(parameterGroup => parameterGroup.id === this.state.groupId)
            if (this.state.parameterGroup) {
                // load group parameters
                const parameters = await this._getConfigurationParameters()
                this.state.parameters = parameters.map(parameter => this._parseParameter(parameter))
            }
        }
        this.$_updateLoading(false)
    }

    async mounted(): Promise<void> {
        await this._loadData()

        // set the parameters group on the sidebar
        this.updateSideBarData()
    }

    private _getParameterValue(parameter: ConfigurationParameterResponse): ConfigurationParameterType {
        let value: ConfigurationParameterType
        if (parameter.value) {
            switch (parameter.type) {
            case ConfigurationParameterTypeEnum.BOOLEAN:
                value = parameter.value === 'true'
                break
            case ConfigurationParameterTypeEnum.NUMBER:
                value = parseFloat(parameter.value)
                break
            case ConfigurationParameterTypeEnum.LIST:
                value = parameter.options.find(option => option.value === parameter.value)
                break
            default:
                value = parameter.value
                break
            }
        }
        return value
    }

    private _parseParameter(parameter: ConfigurationParameterResponse): ConfigurationParameter {
        return {
            ...parameter,
            value: this._getParameterValue(parameter)
        }
    }

    private static _stringifyParameter(parameter: ConfigurationParameter): string {
        let value = ''
        if (!isNil(parameter.value)) {
            if (typeof parameter.value === 'string') {
                value = parameter.value
            } else if (parameter.value.hasOwnProperty('value')) {
                value = (parameter.value as ConfigurationParameterOptionResponse).value
            } else {
                value = JSON.stringify(parameter.value)
            }
        }
        return value
    }

    async addChange(parameter: ConfigurationParameter): Promise<void> {
        let change = this.state.changes.find((c: ConfigurationParameterPatchInBatchRequest) => c.id === parameter.id)
        if (change) {
            change.value = ConfigurationParametersForm._stringifyParameter(parameter)
        } else {
            this.state.changes.push({
                id: parameter.id,
                value: ConfigurationParametersForm._stringifyParameter(parameter)
            })
        }
    }

    private _clear(): void {
        this.state.changes = []
    }

    async patch(): Promise<void> {
        this.$_updateLoading(true)

        if (await this._patchConfigurationParametersBatch()) {
            await sessionStore.loadGlobalParameters()
            await sessionStore.updateLaboratoriesConfiguration()
            await sessionStore.getLaboratoryParams(this.$store.getters.labId)
            this._clear()
        }

        this.$_updateLoading(false)
    }

    private async _getConfigurationParametersGroup(): Promise<ConfigurationParameterGroupResponse[]> {
        let parametersGroups: ConfigurationParameterGroupResponse[] | null = null
        if (this.state.service instanceof ConfigurationParametersGlobalService) {
            parametersGroups = await this.$_toCallSecure(this.state.service.getConfigurationParametersGroup())
        } else if (this.state.service instanceof ConfigurationParametersLocalService) {
            parametersGroups = await this.$_toCallSecure(this.state.service.getConfigurationParametersGroup(this.state.laboratoryId))
        }
        return parametersGroups ?? []
    }

    private async _getConfigurationParameters(): Promise<ConfigurationParameterResponse[]> {
        let parameters: ConfigurationParameterResponse[] | null = null
        if (this.state.service instanceof ConfigurationParametersGlobalService) {
            parameters = await this.$_toCallSecure(this.state.service.getConfigurationParameters(this.state.groupId))
        } else if (this.state.service instanceof ConfigurationParametersLocalService) {
            parameters = await this.$_toCallSecure(this.state.service.getConfigurationParameters(this.state.laboratoryId, this.state.groupId))
        }
        return parameters ?? []
    }

    private async _patchConfigurationParametersBatch(): Promise<EntityIdentifierWithErrorResponse[] | null> {
        let response: EntityIdentifierWithErrorResponse[] | null = null
        if (this.state.service instanceof ConfigurationParametersGlobalService) {
            response = await this.$_toCallSecure(this.state.service.patchConfigurationParametersBatch(this.state.changes))
        } else if (this.state.service instanceof ConfigurationParametersLocalService) {
            response = await this.$_toCallSecure(this.state.service.patchConfigurationParametersBatch(this.state.laboratoryId, this.state.changes))
        }

        if (response) {
            const elements = this.state.changes.length - response.length
            if (elements > 0) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            }
            return response
        }
        return null
    }
}
